import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

let refreshing = null;

async function tryRefresh() {
    if (!refreshing) {
        refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshing = null; });
    }
    return refreshing;
}

function goToLogin() {
    if (window.location.pathname !== "/login") window.location.href = "/login";
}

api.interceptors.response.use(
    (res) => res,
    async (err) => {
        const original = err.config;
        const isAuthCall = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/me"].some((p) => original?.url?.includes(p));
        if (err.response?.status === 401 && original && !original._retried && !isAuthCall) {
            original._retried = true;
            try {
                await tryRefresh();
                return api(original);
            } catch {
                goToLogin();
            }
        }
        return Promise.reject(err);
    }
);

export function formatApiError(err, fallback = "Något gick fel. Försök igen.") {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

async function fetchWithAuth(url, options, retried = false) {
    const res = await fetch(url, options);
    if (res.status === 401 && !retried) {
        try {
            await tryRefresh();
            return fetchWithAuth(url, options, true);
        } catch {
            goToLogin();
            throw new Error("Inte inloggad");
        }
    }
    return res;
}

export async function streamChat(projectId, meddelande, historik, onDelta) {
    const res = await fetchWithAuth(`${API}/projects/${projectId}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meddelande, historik }),
    });
    if (!res.ok) throw new Error(`Fel ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") return;
            try {
                const obj = JSON.parse(payload);
                if (obj.error) throw new Error(obj.error);
                if (obj.delta) onDelta(obj.delta);
            } catch (e) {
                if (e.message && !e.message.includes("JSON")) throw e;
            }
        }
    }
}

export async function streamHubChat(conversationId, meddelande, { onDelta, onDraft, onDraftPending, onDraftError, onIncomplete, onActionPending } = {}) {
    const res = await fetchWithAuth(`${API}/hub/conversations/${conversationId}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meddelande }),
    });
    if (!res.ok) throw new Error(`Fel ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let sawDone = false;
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") { sawDone = true; return; }
            try {
                const obj = JSON.parse(payload);
                if (obj.error) throw new Error(obj.error);
                if (obj.delta) onDelta?.(obj.delta);
                if (obj.draft) onDraft?.(obj.draft);
                if (obj.draft_pending) onDraftPending?.(obj.draft_pending === true ? "presentation" : obj.draft_pending);
                if (obj.action_pending) onActionPending?.(obj.action_pending);
                if (obj.draft_error) onDraftError?.(obj.draft_error);
            } catch (e) {
                if (e.message && !e.message.includes("JSON")) throw e;
            }
        }
    }
    if (!sawDone) onIncomplete?.();
}


export async function streamRoleAgentChat(meddelande, typ, onDelta) {
    const res = await fetch(`${API}/rollagent/session/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meddelande, typ }),
    });
    if (!res.ok) {
        let detail = `Fel ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch { /* behåll status */ }
        throw new Error(detail);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") return;
            try {
                const obj = JSON.parse(payload);
                if (obj.error) throw new Error(obj.error);
                if (obj.delta) onDelta?.(obj.delta);
            } catch (e) {
                if (e.message && !e.message.includes("JSON")) throw e;
            }
        }
    }
}
